import React from 'react';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Atom, Plugins} from '@packages/systems/wfdl';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {getChildren} from '@packages/utilities/data-utils';
import {CLASS_NAME_LOCALES_LIST_ITEM} from '../constants';
import type {LocaleItemContext} from '../types';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';

const InterceptWrapper = ({
  preventDefault,
  children,
}: {
  preventDefault: boolean;
  children: React.ReactNode;
}) => {
  if (preventDefault) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        data-automation-id="locales-item-intercept-wrapper"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    );
  }

  return <>{children}</>;
};

const LocalesItem = Atom.createRender(
  ({attributes, classes, generateAttributes, context, data, renderChild}) => {
    const locale: LocaleItemContext | null | undefined = context.get('locale');
    const runtimeEnvironment = useRuntimeEnvironment();
    const previewMode = usePreviewMode();
    const isInDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
    const inPreviewMode = previewMode && isInDesigner;

    if (!locale) {
      return null;
    }

    const itemClasses = classes.push(CLASS_NAME_LOCALES_LIST_ITEM);
    // the data-wf-locale-display-name attribute is being used to display the locale code
    // as a placeholder on every empty item like we do for Dynamo items
    // with the item name as CSS before content public/css/site/site-designer.less:432
    let itemAttributes = attributes.set('role', 'listitem');

    if (isInDesigner) {
      itemAttributes = itemAttributes.set(
        'data-wf-locale-display-name',
        locale.displayName
      );
    }

    const props = generateAttributes(itemAttributes, itemClasses);
    const children = getChildren(data);

    return (
      <div {...props}>
        <InterceptWrapper preventDefault={inPreviewMode}>
          {children.map((child) => renderChild(child, context))}
        </InterceptWrapper>
      </div>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Locale',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Localization'), iconName('LocalesItem')),
  },
  render: LocalesItem,
  showEmpty: true,
  constraints: {
    pinToParent: true,
    parent: [
      {
        is: 'LocalesList',
        name: 'Element',
        rule: 'RequireOnly',
      },
    ],
  },
  fields: {
    children: TListOfElement,
  },
});

export default config;
